import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';
import Login from './routes/Login.tsx';
import Dashboard from './routes/Dashboard.tsx';
import Canvas from './routes/Canvas.tsx';
import NewDeck from './routes/NewDeck.tsx';
import BuildWatch from './routes/BuildWatch.tsx';
import Library from './routes/Library.tsx';
import Guide from './routes/Guide.tsx';
import { api } from './lib/api.ts';
import { useTheme } from './lib/useTheme.ts';
import { StagingBanner, StagingGuardHost } from './components/StagingChrome.tsx';

/** 'checking' is a real state, not a placeholder: every route below is gated
 *  on the answer, so rendering before it lands would flash the login page at
 *  a signed-in user. */
type AuthState = 'checking' | 'authed' | 'anon';

export default function App() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const location = useLocation();
  const navigate = useNavigate();
  useTheme(); // applies the persisted theme to <html data-theme>; toggles live in the top bars

  useEffect(() => {
    api
      .get('/api/me')
      .then(() => setAuthState('authed'))
      .catch(() => setAuthState('anon'));
  }, []);

  if (authState === 'checking') return <FullPageStatus>Loading…</FullPageStatus>;

  const requireAuth = (el: ReactElement) =>
    authState === 'authed' ? el : <Navigate to="/login" replace state={{ from: location }} />;

  return (
    <>
      {/* Above the router, because there is no shared chrome: every route
          renders its own bar, so a banner inside one of them would be five
          banners. Both render null off staging. */}
      <StagingBanner />
      <StagingGuardHost />
      <Routes>
        <Route
          path="/login"
          element={
            authState === 'authed' ? (
              <Navigate to="/" replace />
            ) : (
              <Login
                onAuthed={() => {
                  setAuthState('authed');
                  navigate('/');
                }}
              />
            )
          }
        />
        <Route path="/" element={requireAuth(<Dashboard />)} />
        {/* Keyed by slug so editor→editor REMOUNTS. Canvas derives its slug from
            the pathname, but PreviewFrame only points a frame at a deck when the
            iframe has no src yet (and useDeckEditor keeps status:'ready' across
            the change), so a reused Canvas left the previous deck's iframe on
            screen — or a blank one — under the new deck's chrome, with the
            nav channel subscribed to a deck the live frame isn't listening on. */}
        <Route path="/editor/*" element={requireAuth(<Canvas key={location.pathname} />)} />
        <Route path="/new" element={requireAuth(<NewDeck />)} />
        {/* Watching ONE headless build. Keyed by jobId so /build/a → /build/b
            (what a retry does) remounts rather than reusing a component still
            holding the old job's stream, slug and transcript. */}
        <Route path="/build/:jobId" element={requireAuth(<BuildWatch key={location.pathname} />)} />
        {/* /library is the Slides shelf; the Bank is /library/bank. The
            /library/slides path stays mounted so links made while Slides had
            its own path still land where they say they will rather than
            falling through to `*`. */}
        <Route path="/library" element={requireAuth(<Library />)} />
        <Route path="/library/bank" element={requireAuth(<Library />)} />
        <Route path="/library/slides" element={requireAuth(<Library />)} />
        <Route path="/guide" element={requireAuth(<Guide />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function FullPageStatus({ children }: { children?: ReactNode }) {
  return (
    <div className="full-page-status">
      <span>{children}</span>
    </div>
  );
}
